import React, { useEffect, useState } from "react";
import { Pencil, Plus, Repeat, Trash2 } from "lucide-react";
import api, { fmtErr } from "@/lib/api";
import { useI18n } from "@/i18n";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";

const empty = {
  type: "expense", amount: "", category: "", description: "",
  frequency: "monthly", day: "1", start_date: new Date().toISOString().slice(0, 10),
  bank_account_id: "none", cari_id: "none", active: true,
};

export default function RecurringPage() {
  const { t, fmt, fmtDate } = useI18n();
  const { canEdit } = useAuth();
  const [rows, setRows] = useState([]);
  const [banks, setBanks] = useState([]);
  const [caris, setCaris] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(empty);
  const [error, setError] = useState("");

  const load = async () => {
    const [r, b, c] = await Promise.all([api.get("/recurring"), api.get("/bank-accounts"), api.get("/cari")]);
    setRows(r.data);
    setBanks(b.data);
    setCaris(c.data);
  };
  useEffect(() => { load().catch(() => {}); }, []);

  const cats = form.type === "income" ? t.catIncome : t.catExpense;

  const openNew = () => { setEditing(null); setForm({ ...empty, start_date: new Date().toISOString().slice(0, 10) }); setError(""); setOpen(true); };
  const openEdit = (r) => {
    setEditing(r);
    setForm({ ...r, day: String(r.day ?? 1), bank_account_id: r.bank_account_id || "none", cari_id: r.cari_id || "none" });
    setError("");
    setOpen(true);
  };

  const save = async () => {
    const payload = {
      ...form,
      amount: parseFloat(form.amount),
      day: parseInt(form.day, 10) || 1,
      bank_account_id: form.bank_account_id === "none" ? null : form.bank_account_id,
      cari_id: form.cari_id === "none" ? null : form.cari_id,
    };
    try {
      if (editing) await api.put(`/recurring/${editing.id}`, payload);
      else await api.post("/recurring", payload);
      setOpen(false);
      load().catch(() => {});
    } catch (e) {
      setError(fmtErr(e));
    }
  };

  const remove = async (id) => {
    if (!window.confirm(t.app.confirmDelete)) return;
    await api.delete(`/recurring/${id}`);
    load().catch(() => {});
  };

  const freqLabel = { weekly: t.rec.weekly, monthly: t.rec.monthly, yearly: t.rec.yearly };

  return (
    <div className="space-y-5" data-testid="recurring-page">
      <div className="flex flex-wrap items-center gap-3">
        <p className="max-w-xl text-sm text-slate-500">{t.rec.desc}</p>
        {canEdit && (
          <Button onClick={openNew} data-testid="recurring-new-button" className="ml-auto">
            <Plus className="mr-2 h-4 w-4" />{t.rec.new}
          </Button>
        )}
      </div>

      <div className="overflow-hidden rounded-2xl border border-slate-200/70 bg-white shadow-sm" data-testid="recurring-table">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-slate-100 text-left font-mono text-[10px] uppercase tracking-widest text-slate-400">
              <th className="px-5 py-3.5 font-medium">{t.app.description}</th>
              <th className="px-5 py-3.5 font-medium">{t.app.category}</th>
              <th className="hidden px-5 py-3.5 font-medium md:table-cell">{t.rec.frequency}</th>
              <th className="px-5 py-3.5 font-medium">{t.rec.nextDate}</th>
              <th className="px-5 py-3.5 text-right font-medium">{t.app.amount}</th>
              <th className="px-5 py-3.5 font-medium">{t.app.status}</th>
              <th className="px-5 py-3.5 text-right font-medium">{t.app.actions}</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-50">
            {rows.length === 0 && (
              <tr><td colSpan={7} className="px-5 py-14 text-center text-sm text-slate-400">{t.app.noData}</td></tr>
            )}
            {rows.map((r) => (
              <tr key={r.id} className="transition-colors hover:bg-slate-50" data-testid={`recurring-row-${r.id}`}>
                <td className="px-5 py-3.5">
                  <div className="flex items-center gap-2.5">
                    <Repeat className="h-4 w-4 shrink-0 text-slate-400" />
                    <span className="font-medium text-slate-900">{r.description || r.category}</span>
                  </div>
                </td>
                <td className="px-5 py-3.5">
                  <span className={`rounded-full px-2.5 py-1 text-xs font-medium ${r.type === "income" ? "bg-emerald-50 text-emerald-600" : "bg-red-50 text-red-500"}`}>
                    {r.category}
                  </span>
                </td>
                <td className="hidden px-5 py-3.5 text-slate-500 md:table-cell">
                  {freqLabel[r.frequency]}{r.frequency === "monthly" && r.day ? ` · ${r.day}` : ""}
                </td>
                <td className="whitespace-nowrap px-5 py-3.5 font-mono text-xs text-slate-500">{fmtDate(r.next_date)}</td>
                <td className={`tnum whitespace-nowrap px-5 py-3.5 text-right font-mono font-medium ${r.type === "income" ? "text-positive" : "text-negative"}`}>
                  {r.type === "income" ? "+" : "−"}{fmt(r.amount)}
                </td>
                <td className="px-5 py-3.5">
                  <span className={`rounded-full px-2.5 py-1 text-xs font-medium ${r.active ? "bg-emerald-50 text-emerald-600" : "bg-slate-100 text-slate-500"}`}>
                    {r.active ? t.rec.active : t.rec.paused}
                  </span>
                </td>
                <td className="whitespace-nowrap px-5 py-3.5 text-right">
                  {canEdit && (
                    <>
                      <button onClick={() => openEdit(r)} aria-label={t.app.edit} data-testid={`recurring-edit-${r.id}`}
                        className="rounded-md p-1.5 text-slate-400 hover:bg-slate-100 hover:text-slate-900">
                        <Pencil className="h-4 w-4" />
                      </button>
                      <button onClick={() => remove(r.id)} aria-label={t.app.delete} data-testid={`recurring-delete-${r.id}`}
                        className="rounded-md p-1.5 text-slate-400 hover:bg-red-50 hover:text-red-600">
                        <Trash2 className="h-4 w-4" />
                      </button>
                    </>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="sm:max-w-lg" data-testid="recurring-dialog">
          <DialogHeader><DialogTitle>{editing ? t.rec.edit : t.rec.new}</DialogTitle></DialogHeader>
          <div className="grid gap-4 py-2">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>{t.app.income} / {t.app.expense}</Label>
                <Select value={form.type} onValueChange={(v) => setForm({ ...form, type: v, category: "" })} data-testid="recurring-type-select">
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="income">{t.app.income}</SelectItem>
                    <SelectItem value="expense">{t.app.expense}</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>{t.app.amount}</Label>
                <Input type="number" step="0.01" min="0" value={form.amount}
                  onChange={(e) => setForm({ ...form, amount: e.target.value })} data-testid="recurring-amount-input" />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>{t.app.category}</Label>
                <Select value={form.category || undefined} onValueChange={(v) => setForm({ ...form, category: v })} data-testid="recurring-category-select">
                  <SelectTrigger><SelectValue placeholder={t.app.category} /></SelectTrigger>
                  <SelectContent>{cats.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>{t.app.description}</Label>
                <Input value={form.description || ""} onChange={(e) => setForm({ ...form, description: e.target.value })} data-testid="recurring-description-input" />
              </div>
            </div>
            <div className="grid grid-cols-3 gap-4">
              <div className="space-y-2">
                <Label>{t.rec.frequency}</Label>
                <Select value={form.frequency} onValueChange={(v) => setForm({ ...form, frequency: v })} data-testid="recurring-frequency-select">
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="weekly">{t.rec.weekly}</SelectItem>
                    <SelectItem value="monthly">{t.rec.monthly}</SelectItem>
                    <SelectItem value="yearly">{t.rec.yearly}</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              {form.frequency === "monthly" && (
                <div className="space-y-2">
                  <Label>{t.rec.dayOfMonth}</Label>
                  <Input type="number" min="1" max="31" value={form.day}
                    onChange={(e) => setForm({ ...form, day: e.target.value })} data-testid="recurring-day-input" />
                </div>
              )}
              <div className="space-y-2">
                <Label>{t.rec.startDate}</Label>
                <Input type="date" value={form.start_date || ""}
                  onChange={(e) => setForm({ ...form, start_date: e.target.value })} data-testid="recurring-startdate-input" />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>{t.app.bank}</Label>
                <Select value={form.bank_account_id} onValueChange={(v) => setForm({ ...form, bank_account_id: v })} data-testid="recurring-bank-select">
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">—</SelectItem>
                    {banks.map((b) => <SelectItem key={b.id} value={b.id}>{b.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>{t.app.cari}</Label>
                <Select value={form.cari_id} onValueChange={(v) => setForm({ ...form, cari_id: v })} data-testid="recurring-cari-select">
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">—</SelectItem>
                    {caris.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <Switch checked={form.active} onCheckedChange={(v) => setForm({ ...form, active: v })} data-testid="recurring-active-switch" />
              <span className="text-sm text-slate-600">{t.rec.active}</span>
            </div>
            {error && <p className="rounded-md border border-red-100 bg-red-50 px-3.5 py-2.5 text-sm text-red-600" data-testid="recurring-error">{error}</p>}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)} data-testid="recurring-cancel-button">{t.app.cancel}</Button>
            <Button onClick={save} disabled={!form.amount || !form.category || !form.start_date} data-testid="recurring-save-button">{t.app.save}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
